/**
 * DropboxSyncIndicator
 *
 * Shows Dropbox sync status on invoice detail page.
 * - Connected + synced + path: "Synced to Dropbox" as clickable link (opens the file)
 * - Connected + synced (no path): "Synced to Dropbox" with timestamp (legacy, non-clickable)
 * - Connected + not synced: "Not yet synced" note
 * - Not connected: renders nothing (no noise)
 */
import { ExternalLink } from 'lucide-react';
import { useDropboxStatus } from '@/hooks/useDropbox';

interface DropboxSyncIndicatorProps {
  dropboxSyncedAt?: string | null;
  /**
   * Deterministic Dropbox path (e.g. "/BuildGravity Invoices/2026/INV-001 - Acme.pdf").
   * When set alongside dropboxSyncedAt, the indicator becomes a clickable link that
   * opens the file in Dropbox. When null/undefined, falls back to static text (back-compat
   * with invoices synced before this field existed on the API).
   */
  dropboxPath?: string | null;
}

// Simple Dropbox box icon (inline SVG — lucide has no Dropbox icon)
function DropboxIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6 2L1 5.5 6 9l5-3.5L6 2zM18 2l-5 3.5 5 3.5 5-3.5L18 2zM1 12.5L6 16l5-3.5-5-3.5-5 3.5zM13 12.5L18 16l5-3.5-5-3.5-5 3.5zM6 17.5L11 21l5-3.5-5-3.5-5 3.5z" />
    </svg>
  );
}

export function DropboxSyncIndicator({ dropboxSyncedAt, dropboxPath }: DropboxSyncIndicatorProps) {
  const { data: dropboxStatus } = useDropboxStatus();

  if (!dropboxStatus?.connected) return null;

  if (dropboxSyncedAt) {
    const date = new Date(dropboxSyncedAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

    // Clickable link path: opens the actual file in Dropbox web UI.
    // Christa feedback (2026-04-08): wants to click the invoice and see the file.
    if (dropboxPath) {
      const href = `https://www.dropbox.com/home${dropboxPath}`;
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title="Open in Dropbox"
          className="inline-flex items-center gap-1.5 text-sm text-green-700 hover:text-green-800 hover:underline"
          data-testid="dropbox-sync-indicator"
        >
          <DropboxIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="font-medium">Synced to Dropbox</span>
          <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="text-muted-foreground text-xs">&middot; {date}</span>
        </a>
      );
    }

    // Fallback (no path yet — e.g. older synced invoice before this field existed).
    return (
      <div className="flex items-center gap-1.5 text-sm text-green-700" data-testid="dropbox-sync-indicator">
        <DropboxIcon className="h-3.5 w-3.5 shrink-0" />
        <span className="font-medium">Synced to Dropbox</span>
        <span className="text-muted-foreground text-xs">&middot; {date}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="dropbox-sync-indicator">
      <DropboxIcon className="h-3.5 w-3.5 shrink-0" />
      <span>Not yet synced</span>
      <span className="text-xs">(syncs on send or download)</span>
    </div>
  );
}
